// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component'
import type { OrganizationResponse } from '@umd-dit-ai-workspace/workspaces-component'
import { discoverApi } from '@/api/discoverApi'
import type { WorkspaceRequestResponse } from '@/api/discoverApi'
import { CheckCircle, XCircle, Clock, User, Building2, MessageSquare, RefreshCw, Search } from 'lucide-react'

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all'

const STATUS_LABELS: Record<string, { label: string; classes: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    classes: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    icon: <Clock className="h-3 w-3" />,
  },
  approved: {
    label: 'Approved',
    classes: 'bg-green-50 text-green-700 border-green-200',
    icon: <CheckCircle className="h-3 w-3" />,
  },
  rejected: {
    label: 'Rejected',
    classes: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle className="h-3 w-3" />,
  },
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_LABELS[status] ?? { label: status, classes: 'bg-gray-100 text-gray-600 border-gray-200', icon: null }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.classes}`}>
      {s.icon}
      {s.label}
    </span>
  )
}

function RequestCard({
  req,
  orgs,
  onApprove,
  onReject,
  resolving,
}: {
  req: WorkspaceRequestResponse
  orgs: OrganizationResponse[]
  onApprove: (id: string, orgId?: string, wsName?: string, wsDescription?: string) => void
  onReject: (id: string) => void
  resolving: string | null
}) {
  const isPending = req.status === 'pending'
  const isResolving = resolving === req.request_id
  const isCreateRequest = req.request_type === 'create'
  const singleOrg = orgs.length === 1 ? orgs[0] : null
  const [approveOrgId, setApproveOrgId] = useState(req.org_id || singleOrg?.orgId || '')
  const [editWsName, setEditWsName] = useState(req.ws_name ?? '')
  const [editWsDescription, setEditWsDescription] = useState(req.ws_description ?? '')
  const estFormat: Intl.DateTimeFormatOptions = { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'short' }
  const parseUTC = (ts: string) => new Date(ts.endsWith('Z') ? ts : ts + 'Z')
  const createdAt = parseUTC(req.created_at).toLocaleString('en-US', estFormat)
  const resolvedAt = req.resolved_at ? parseUTC(req.resolved_at).toLocaleString('en-US', estFormat) : null

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
              req.request_type === 'join'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-purple-50 text-purple-700 border-purple-200'
            }`}>
              {req.request_type === 'join' ? 'Join Request' : 'New Workspace'}
            </span>
            <StatusBadge status={req.status} />
          </div>
          {req.request_type === 'join' && (
            <p className="text-sm font-semibold text-gray-900 mt-1">{req.ws_id}</p>
          )}
          {req.request_type === 'create' && !isPending && (
            <p className="text-sm font-semibold text-gray-900 mt-1">{req.ws_name ?? req.ws_id}</p>
          )}
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0">{createdAt}</span>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-start gap-2 text-gray-600">
          <User className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="truncate font-medium">{req.display_name ?? req.user_id}</p>
            {req.display_name && (
              <p className="truncate text-xs text-gray-400">{req.user_id}</p>
            )}
            {req.primary_title && (
              <p className="truncate text-xs text-gray-500">{req.primary_title}</p>
            )}
            {req.department && (
              <p className="truncate text-xs text-gray-500">{req.department}</p>
            )}
          </div>
        </div>
        {req.request_type === 'join' && (
          <div className="flex items-center gap-2 text-gray-600">
            <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{req.org_id}</span>
          </div>
        )}
      </div>

      {req.message && (
        <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
          <MessageSquare className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <span>{req.message}</span>
        </div>
      )}

      {resolvedAt && req.resolved_by && (
        <p className="text-xs text-gray-400">
          {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.resolved_by} on {resolvedAt}
        </p>
      )}

      {/* Actions */}
      {isPending && (
        <div className="space-y-2 pt-1">
          {isCreateRequest && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Workspace name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editWsName}
                  onChange={e => setEditWsName(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Description
                  <span className="text-gray-400 font-normal ml-1">(optional)</span>
                </label>
                <input
                  type="text"
                  value={editWsDescription}
                  onChange={e => setEditWsDescription(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Org <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">(which org to create this workspace in)</span>
              </label>
              {singleOrg ? (
                <p className="text-sm text-gray-700 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                  {singleOrg.name} ({singleOrg.orgId})
                </p>
              ) : (
                <select
                  value={approveOrgId}
                  onChange={e => setApproveOrgId(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                >
                  <option value="">— select an org —</option>
                  {orgs.map(org => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name} ({org.orgId})
                    </option>
                  ))}
                </select>
              )}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(req.request_id, isCreateRequest ? approveOrgId.trim() : undefined, isCreateRequest ? editWsName.trim() : undefined, isCreateRequest ? editWsDescription.trim() : undefined)}
              disabled={isResolving || (isCreateRequest && (!approveOrgId.trim() || !editWsName.trim()))}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResolving ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Approve
            </button>
            <button
              onClick={() => onReject(req.request_id)}
              disabled={isResolving}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminRequestsPage() {
  const { isAdmin, currentWorkspace, accessibleOrganizations } = useAuth()
  const isWorkspaceAdmin = isAdmin || currentWorkspace?.role?.toLowerCase() === 'admin'
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [searchQuery, setSearchQuery] = useState('')
  const [resolving, setResolving] = useState<string | null>(null)

  // Redirect non-admins (global admins and workspace admins can access)
  if (!isWorkspaceAdmin) return <Navigate to="/" replace />

  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['workspaceRequests', statusFilter],
    queryFn: () => discoverApi.listRequests(statusFilter === 'all' ? undefined : statusFilter),
    staleTime: 30_000,
  })

  const resolveMutation = useMutation({
    mutationFn: ({ id, action, orgId, wsName, wsDescription }: { id: string; action: 'approve' | 'reject'; orgId?: string; wsName?: string; wsDescription?: string }) =>
      discoverApi.resolveRequest(id, action, orgId, wsName, wsDescription),
    onMutate: ({ id }) => setResolving(id),
    onSettled: () => {
      setResolving(null)
      queryClient.invalidateQueries({ queryKey: ['workspaceRequests'] })
    },
  })

  const pending = requests.filter(r => r.status === 'pending').length

  const filteredRequests = searchQuery.trim()
    ? requests.filter(r => {
        const q = searchQuery.toLowerCase()
        return (
          r.user_id.toLowerCase().includes(q) ||
          (r.display_name ?? '').toLowerCase().includes(q) ||
          (r.ws_id ?? '').toLowerCase().includes(q) ||
          (r.ws_name ?? '').toLowerCase().includes(q) ||
          (r.department ?? '').toLowerCase().includes(q)
        )
      })
    : requests

  const FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'pending', label: `Pending${pending > 0 ? ` (${pending})` : ''}` },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'all', label: 'All' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-8 rounded-xl shadow-lg flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Workspace Requests</h1>
          <p className="text-gray-500">
            Review and action join and creation requests from users.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                statusFilter === f.value
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, username, workspace, or department…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Clock className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                {searchQuery.trim() ? `No results for "${searchQuery}"` : `No ${statusFilter === 'all' ? '' : statusFilter} requests`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredRequests.map(req => (
                <RequestCard
                  key={req.request_id}
                  req={req}
                  orgs={accessibleOrganizations}
                  resolving={resolving}
                  onApprove={(id, orgId, wsName, wsDescription) => resolveMutation.mutate({ id, action: 'approve', orgId, wsName, wsDescription })}
                  onReject={id => resolveMutation.mutate({ id, action: 'reject' })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
